import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AppFooter from '@core/components/AppFooter';
import AuthBackToHome from '@features/auth/components/AuthBackToHome';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { loginAsync } from '@features/auth/services/loginSvc';
import { LoginRequest } from '@features/auth/types/loginRequest';
import { ErrorResponse } from '@core/types/errorResponse';
import { SESSION_STORAGE_KEYS } from '@features/auth/constants/sessionStorageKeys';
import { isSafePostLoginRedirect } from '@features/auth/utils/clearClientSessionArtifacts';
// TypeScript may not have declarations for CSS modules imported for side-effects.
// @ts-ignore
import './login.css';
import loginImage from './ntcog-logo.png';
import InfoTooltip from '@core/components/InfoTooltip';

type FieldErrors = Partial<Record<keyof LoginRequest, string[]>>;

const Login: React.FC = () => {
  const [form, setForm] = useState<LoginRequest>({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const showError = useApiError();
  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.EXPLICIT_LOGOUT);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Clear error when field changes
    if (formErrors[name as keyof LoginRequest]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name as keyof LoginRequest];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const response = await loginAsync(form);

      if (response.isSuccess()) {
        setFormErrors({});
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.EXPLICIT_LOGOUT);
        const redirectTo = sessionStorage.getItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
        navigate(
          redirectTo && isSafePostLoginRedirect(redirectTo) ? redirectTo : '/entities'
        );
      } else {
        const error = response.getError() as ErrorResponse;
        setFormErrors((error?.errors as FieldErrors) ?? {});
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper min-vh-100 d-flex flex-column">
      <AuthBackToHome />
      <main
        id="main"
        className="flex-grow-1 d-flex align-items-center justify-content-center py-4"
        tabIndex={-1}
        data-testid="login-page"
      >
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="login-card shadow-lg border-0" data-testid="login-card">
                <Row className="g-0">
                  {/* Left Image Section */}
                  <Col
                    md={6}
                    className="d-none d-md-flex align-items-center justify-content-center login-image bg-light"
                    data-testid="login-illustration-panel"
                  >
                    <img src={loginImage} alt="NTCOG Logo" className="img-fluid" />
                  </Col>

                  {/* Right Form Section */}
                  <Col md={6} className="d-flex align-items-center">
                    <Card.Body className="p-4 p-md-5 w-100 auth-card__body">
                      <h1 className="mb-4 text-center fw-bold text-dark auth-page-heading">Welcome Back</h1>

                      <Form
                        noValidate
                        onSubmit={handleSubmit}
                        className="login-form"
                        aria-label="Login form"
                        data-testid="login-form"
                      >
                        <Form.Group className="mb-4" controlId="email">
                          <Form.Label>Email address<InfoTooltip text="Enter the email address you use to sign in." /></Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            placeholder="name@example.com"
                            value={form.email}
                            onChange={handleChange}
                            autoComplete="email"
                            isInvalid={!!formErrors.email}
                            data-testid="login-email-input"
                          />
                          <Form.Control.Feedback type="invalid" role="alert">
                            {formErrors.email?.[0]}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="password">
                          <Form.Label>Password<InfoTooltip text="Enter your account password." /></Form.Label>
                          <Form.Control
                            type="password"
                            name="password"
                            placeholder="Password"
                            value={form.password}
                            onChange={handleChange}
                            autoComplete="current-password"
                            isInvalid={!!formErrors.password}
                            data-testid="login-password-input"
                          />
                          <Form.Control.Feedback type="invalid" role="alert">
                            {formErrors.password?.[0]}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          className="w-100 mb-3 login-btn"
                          disabled={loading}
                          aria-busy={loading}
                          data-testid="login-submit-button"
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              />
                              Logging in…
                            </>
                          ) : (
                            'Login'
                          )}
                        </Button>

                        <div className="auth-secondary-link-wrap">
                          <Button
                            variant="link"
                            className="text-muted p-0 forgot-password auth-secondary-link"
                            onClick={() => navigate('/forgot-password')}
                            data-testid="login-forgot-password-link"
                          >
                            Forgot password?
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>
      <AppFooter />
    </div>
  );
};

export default Login;
